import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

/**
 * Reusable list of related service / page links - improves internal linking & SEO.
 * Pass `currentPath` to omit current page from the list.
 */
const ALL_LINKS = [
  {
    path: "/end-of-tenancy-cleaning-london",
    label: "End of Tenancy Cleaning",
    summary:
      "Landlord-approved checklists, inventory-ready finishes and a written re-clean guarantee for tenants moving out across London.",
  },
  {
    path: "/deep-cleaning-london",
    label: "Deep Cleaning",
    summary:
      "One-off, top-to-bottom deep cleans for residents tackling neglected areas, post-renovation dust or a periodic reset of the home.",
  },
  {
    path: "/carpet-cleaning-london",
    label: "Carpet Cleaning",
    summary:
      "Hot-water extraction carpet cleaning that lifts ground-in dirt, pet odours and stains — fast-drying and inventory friendly.",
  },
  {
    path: "/oven-cleaning-london",
    label: "Oven Cleaning",
    summary:
      "Dip-tank degreasing for single, double and range ovens, hobs, extractors and microwaves — restored to a sparkling, sellable finish.",
  },
];

export const RelatedLinks = ({
  currentPath,
  heading = "Explore Our Other London Cleaning Services",
  subheading = "Combine services for a bigger discount, or pick the single clean that fits your move-out, inventory or maintenance schedule.",
  extraLinks = [],
  background = "slate",
}) => {
  const items = [
    ...ALL_LINKS.filter((l) => l.path !== currentPath),
    ...extraLinks,
  ];

  const bgClass = background === "slate" ? "bg-slate-50" : "bg-white";

  return (
    <section
      className={`py-20 md:py-24 ${bgClass}`}
      data-testid="related-links-section"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-12"
        >
          <span className="text-teal font-semibold text-sm uppercase tracking-wider">
            Related Services
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-4">
            {heading}
          </h2>
          <p className="text-slate-600 leading-relaxed">{subheading}</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((link, i) => (
            <motion.div
              key={link.path}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link
                to={link.path}
                className="block bg-white rounded-2xl p-7 hover:bg-navy hover:text-white transition-colors duration-300 group h-full border border-slate-100"
                data-testid={`related-link-${link.path}`}
              >
                <h3 className="font-heading text-xl font-semibold text-navy group-hover:text-white mb-3 transition-colors">
                  {link.label}
                </h3>
                <p className="text-slate-600 group-hover:text-slate-300 text-sm leading-relaxed mb-5 transition-colors">
                  {link.summary}
                </p>
                <span className="inline-flex items-center gap-1.5 font-medium text-sm text-teal group-hover:text-gold transition-colors">
                  Learn more
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
